import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cx } from '../../lib/cx';
import { Icon, type IconName } from '../Icon';

import styles from './Button.module.css';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'text';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * primary: the one main action; secondary: singing and sound; ghost: everything
   * else; text: an action inside a line or a dense list, with no surface of its own.
   */
  variant?: ButtonVariant;
  /** sm: for toolbars and rows of a table. md follows the device. */
  size?: ButtonSize;
  /** Shown before the label. */
  icon?: IconName;
  /**
   * The action is on right now (playing, listening, chosen). Announced as a pressed
   * toggle; leave undefined for a button that is not a toggle.
   */
  pressed?: boolean;
  /** Take the whole width of the container: how actions stack on a phone. */
  fullWidth?: boolean;
  children: ReactNode;
}

/**
 * The classes of a button, for an element that is not one: a link that leads
 * somewhere but reads as an action ("New lesson", "Take the test"). It then looks
 * exactly like the `Button` beside it, on every device.
 */
export function buttonClassName({ variant = 'primary', size = 'md', fullWidth = false }: { variant?: ButtonVariant; size?: ButtonSize; fullWidth?: boolean } = {}): string {
  return cx(styles.button, styles[variant], size === 'sm' && styles.sm, fullWidth && styles.fullWidth);
}

/** A labelled action. Its height follows the device: 36, 40 or 44 px (`sm`: 32 px everywhere). */
export function Button({
  variant = 'primary',
  size = 'md',
  icon,
  pressed,
  fullWidth = false,
  className,
  type = 'button',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      className={cx(buttonClassName({ variant, size, fullWidth }), className)}
      aria-pressed={pressed}
      data-variant={variant}
      {...rest}
    >
      {icon ? <Icon name={icon} size="var(--hs-button-icon)" className={styles.icon} /> : null}
      <span className={styles.label}>{children}</span>
    </button>
  );
}
